import type { Invitation, Membership, Role, User } from "../models";
import { ALL_PERMISSION_KEYS, PERMISSIONS } from "../../rbac/permissions";
import { EMPLOYEE, MANAGER, DEALS_MANAGER, LEADS_MANAGER, ATTENDANCE_MANAGER, FINANCE_MANAGER, ASSET_MANAGER, HELP_DESK_MANAGER, RECRUITER, IT_ADMIN } from "../../rbac/roles";
import { DEMO_TENANT_ID } from "../../auth/auth.constants";

const now = () => new Date().toISOString();

function permId(key: string): string {
  return `perm-${key.replace(/\./g, "-")}`;
}

export const SEED_IDENTITY_PERMISSIONS = ALL_PERMISSION_KEYS.map((key) => {
  const [resource, action] = key.split(".");
  return {
    id: permId(key),
    resource: resource ?? key,
    action: action ?? "read",
    key,
  };
});

function rolePerms(codes: string[]): string[] {
  return codes;
}

function roleFromSystem(
  id: string,
  code: string,
  name: string,
  permissions: { key: string }[],
  description?: string
): Role {
  return {
    id,
    code,
    name,
    description,
    permissions: rolePerms(permissions.map((p) => p.key)),
    system: true,
  };
}

export const SEED_IDENTITY_ROLES: Role[] = [
  {
    id: "role-tenant-admin",
    code: "TENANT_ADMIN",
    name: "Tenant Admin",
    description: "Administrateur tenant",
    permissions: rolePerms([
      ...ALL_PERMISSION_KEYS.filter((k) => !k.startsWith("platform.")),
    ]),
    system: true,
  },
  {
    id: "role-hr-manager",
    code: "HR_MANAGER",
    name: "HR Manager",
    permissions: rolePerms([
      "employee.read",
      "employee.create",
      "employee.update",
      "employee.delete",
      "department.read",
      "department.create",
      "department.update",
      "position.read",
      "position.create",
      "position.update",
      "organization.read",
      "leave.read",
      "leave.create",
      "leave.update",
      "leave.approve",
      "leave.manage",
      "attendance.read",
      "attendance.create",
      "attendance.update",
      "attendance.manage",
      "attendance.analytics",
      "schedule.read",
      "schedule.create",
      "schedule.update",
      "schedule.manage",
      "performance.read",
      "performance.create",
      "performance.update",
      "performance.review",
      "performance.manage",
      "compensation.read",
      "payroll.read",
      "payroll.run",
      "payroll.adjust",
      "bonus.read",
      "bonus.manage",
      "training.read",
      "training.manage",
      "course.read",
      "course.manage",
      "certification.read",
      "certification.manage",
      "career.read",
      "career.manage",
      "promotion.read",
      "promotion.manage",
      "succession.read",
      "succession.manage",
      "recruitment.read",
      "recruitment.manage",
      "candidate.read",
      "candidate.manage",
      "hiring.manage",
      "asset.read",
      "ticket.read",
      "user.read",
      "user.create",
      "user.update",
      "role.read",
      "invitation.read",
      "invitation.create",
      "action_validation.read",
      "action_validation.approve",
    ]),
    system: true,
  },
  {
    id: "role-payroll-manager",
    code: "PAYROLL_MANAGER",
    name: "Payroll Manager",
    permissions: rolePerms([
      "employee.read",
      "payroll.read",
      "payroll.run",
      "payroll.adjust",
      "compensation.read",
    ]),
    system: true,
  },
  {
    id: "role-platform-owner",
    code: "PLATFORM_OWNER",
    name: "Platform Owner",
    permissions: rolePerms(ALL_PERMISSION_KEYS),
    system: true,
  },
  {
    id: "role-platform-admin",
    code: "PLATFORM_ADMIN",
    name: "Platform Admin",
    permissions: rolePerms([
      "platform.manage",
      "tenant.manage",
      "subscription.manage",
      "billing.manage",
      "support.manage",
      "user.read",
      "user.create",
      "user.update",
      "user.delete",
      "role.read",
      "role.manage",
      "invitation.read",
      "invitation.create",
      "invitation.cancel",
      "department.read",
      "department.create",
      "department.update",
      "department.delete",
      "position.read",
      "position.create",
      "position.update",
      "position.delete",
      "organization.read",
      "organization.manage",
      "leave.read",
      "leave.create",
      "leave.update",
      "leave.approve",
      "leave.manage",
      "attendance.read",
      "attendance.create",
      "attendance.update",
      "attendance.manage",
      "attendance.analytics",
      "schedule.read",
      "schedule.create",
      "schedule.update",
      "schedule.manage",
      "performance.read",
      "performance.create",
      "performance.update",
      "performance.review",
      "performance.manage",
      "compensation.read",
      "compensation.manage",
      "bonus.read",
      "bonus.manage",
      "payroll.adjust",
      "training.read",
      "training.manage",
      "course.read",
      "course.manage",
      "certification.read",
      "certification.manage",
      "career.read",
      "career.manage",
      "promotion.read",
      "promotion.manage",
      "succession.read",
      "succession.manage",
      "recruitment.read",
      "recruitment.manage",
      "candidate.read",
      "candidate.manage",
      "hiring.manage",
      "asset.read",
      "asset.create",
      "asset.update",
      "asset.assign",
      "asset.maintain",
      "asset.retire",
      "ticket.read",
      "ticket.create",
      "ticket.update",
      "ticket.assign",
      "ticket.close",
      "incident.read",
      "incident.create",
      "incident.resolve",
      "sla.read",
      "sla.manage",
      "problem.read",
      "problem.create",
      "problem.update",
      "problem.close",
      "change.read",
      "change.create",
      "change.approve",
      "project.read",
      "project.create",
      "project.manage",
      "knowledge.read",
      "knowledge.create",
      "knowledge.publish",
      "customer.read",
      "customer.create",
      "contract.read",
      "contract.create",
      "contract.manage",
      "billing.read",
      "billing.manage",
      "service.read",
      "service.manage",
      "request.read",
      "request.create",
      "request.approve",
      "request.complete",
      "approval.read",
      "approval.manage",
      "automation.read",
    ]),
    system: true,
  },
  roleFromSystem(
    EMPLOYEE.id,
    EMPLOYEE.slug,
    EMPLOYEE.name,
    EMPLOYEE.permissions,
    EMPLOYEE.description
  ),
  roleFromSystem(
    MANAGER.id,
    MANAGER.slug,
    MANAGER.name,
    MANAGER.permissions,
    MANAGER.description
  ),
  roleFromSystem(
    DEALS_MANAGER.id,
    DEALS_MANAGER.slug,
    DEALS_MANAGER.name,
    DEALS_MANAGER.permissions,
    DEALS_MANAGER.description
  ),
  roleFromSystem(
    LEADS_MANAGER.id,
    LEADS_MANAGER.slug,
    LEADS_MANAGER.name,
    LEADS_MANAGER.permissions,
    LEADS_MANAGER.description
  ),
  roleFromSystem(
    RECRUITER.id,
    RECRUITER.slug,
    RECRUITER.name,
    RECRUITER.permissions,
    RECRUITER.description
  ),
  roleFromSystem(
    ATTENDANCE_MANAGER.id,
    ATTENDANCE_MANAGER.slug,
    ATTENDANCE_MANAGER.name,
    ATTENDANCE_MANAGER.permissions,
    ATTENDANCE_MANAGER.description
  ),
  roleFromSystem(
    FINANCE_MANAGER.id,
    FINANCE_MANAGER.slug,
    FINANCE_MANAGER.name,
    FINANCE_MANAGER.permissions,
    FINANCE_MANAGER.description
  ),
  roleFromSystem(
    IT_ADMIN.id,
    IT_ADMIN.slug,
    IT_ADMIN.name,
    IT_ADMIN.permissions,
    IT_ADMIN.description
  ),
  roleFromSystem(
    ASSET_MANAGER.id,
    ASSET_MANAGER.slug,
    ASSET_MANAGER.name,
    ASSET_MANAGER.permissions,
    ASSET_MANAGER.description
  ),
  roleFromSystem(
    HELP_DESK_MANAGER.id,
    HELP_DESK_MANAGER.slug,
    HELP_DESK_MANAGER.name,
    HELP_DESK_MANAGER.permissions,
    HELP_DESK_MANAGER.description
  ),
  {
    id: "role-customer-user",
    code: "CUSTOMER_USER",
    name: "Customer User",
    description: "Utilisateur portail client MSP",
    permissions: rolePerms([
      "portal.ticket.read",
      "portal.asset.read",
      "portal.contract.read",
      "portal.invoice.read",
      "portal.user.read",
      "request.read",
      "request.create",
    ]),
    system: true,
  },
];

export const SEED_IDENTITY_USERS: User[] = [
  {
    id: "user-tenant-admin",
    email: "admin@itaas.local",
    firstName: "Administrateur",
    lastName: "ITaaS",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
    avatar: "assets/img/profiles/avatar-02.jpg",
  },
  {
    id: "user-admin-demo",
    email: "admin@itaas.demo",
    firstName: "Admin",
    lastName: "Demo",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
    avatar: "assets/img/profiles/avatar-02.jpg",
  },
  {
    id: "user-hr-demo",
    email: "hr@itaas.demo",
    firstName: "Roger",
    lastName: "NIKUNA",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-hr",
    email: "hr@itaas.local",
    firstName: "Roger",
    lastName: "NIKUNA",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-payroll",
    email: "payroll@itaas.local",
    firstName: "Trésor",
    lastName: "ETUKA",
    status: "active",
    emailVerified: true,
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-platform-owner",
    email: "super@itaas.local",
    firstName: "Super",
    lastName: "Administrateur",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-inactive",
    email: "inactive@itaas.local",
    firstName: "Utilisateur",
    lastName: "Inactif",
    status: "inactive",
    emailVerified: false,
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-john",
    email: "john@itaas.local",
    firstName: "Hervé",
    lastName: "KASHAMA",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-employee",
    email: "employee@itaas.local",
    firstName: "Obed",
    lastName: "NKASHAMA",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-manager",
    email: "manager@itaas.local",
    firstName: "Elysée",
    lastName: "KAJA",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-customer",
    email: "customer@itaas.local",
    firstName: "Client",
    lastName: "Demo",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-deals",
    email: "deals@itaas.local",
    firstName: "Responsable",
    lastName: "Deals",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-leads",
    email: "leads@itaas.local",
    firstName: "Responsable",
    lastName: "Leads",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-recruitment",
    email: "recruitment@itaas.local",
    firstName: "Chargé",
    lastName: "Recrutement",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-attendance",
    email: "attendance@itaas.local",
    firstName: "Responsable",
    lastName: "Présence",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-finance",
    email: "finance@itaas.local",
    firstName: "Erudit",
    lastName: "KONDE",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-itadmin",
    email: "itadmin@itaas.local",
    firstName: "Admin",
    lastName: "IT",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-asset",
    email: "asset@itaas.local",
    firstName: "Gestionnaire",
    lastName: "Actifs",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "user-helpdesk",
    email: "helpdesk@itaas.local",
    firstName: "Agent",
    lastName: "Help Desk",
    status: "active",
    emailVerified: true,
    lastLoginAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
];

export const SEED_MEMBERSHIPS: Membership[] = [
  {
    id: "mem-admin",
    userId: "user-tenant-admin",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-tenant-admin",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-admin-demo",
    userId: "user-admin-demo",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-tenant-admin",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-hr-demo",
    userId: "user-hr-demo",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-hr-manager",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-hr",
    userId: "user-hr",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-hr-manager",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-payroll",
    userId: "user-payroll",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-payroll-manager",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-platform",
    userId: "user-platform-owner",
    tenantId: "tenant-platform",
    roleId: "role-platform-owner",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-inactive",
    userId: "user-inactive",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-hr-manager",
    status: "inactive",
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-john",
    userId: "user-john",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-hr-manager",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-employee",
    userId: "user-employee",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-employee",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-manager",
    userId: "user-manager",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-manager",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-customer",
    userId: "user-customer",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-customer-user",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-deals",
    userId: "user-deals",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-deals-manager",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-leads",
    userId: "user-leads",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-leads-manager",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-recruitment",
    userId: "user-recruitment",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-recruiter",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-attendance",
    userId: "user-attendance",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-attendance-manager",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-finance",
    userId: "user-finance",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-finance-manager",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-itadmin",
    userId: "user-itadmin",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-it-admin",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-asset",
    userId: "user-asset",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-asset-manager",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
  {
    id: "mem-helpdesk",
    userId: "user-helpdesk",
    tenantId: DEMO_TENANT_ID,
    roleId: "role-help-desk-manager",
    status: "active",
    joinedAt: now(),
    createdAt: now(),
    updatedAt: now(),
  },
];

const inviteExpiry = new Date();
inviteExpiry.setDate(inviteExpiry.getDate() + 7);

export const SEED_INVITATIONS: Invitation[] = [
  {
    id: "inv-001",
    tenantId: DEMO_TENANT_ID,
    email: "nouveau@itaas.local",
    roleId: "role-hr-manager",
    token: "mock-token-001",
    status: "pending",
    expiresAt: inviteExpiry.toISOString(),
    createdAt: now(),
  },
  {
    id: "inv-002",
    tenantId: DEMO_TENANT_ID,
    email: "invite2@itaas.local",
    roleId: "role-payroll-manager",
    token: "mock-token-002",
    status: "pending",
    expiresAt: inviteExpiry.toISOString(),
    createdAt: now(),
  },
];

/** Map email demo → role code pour compat legacy */
export const DEMO_EMAIL_ROLE_MAP: Record<string, string> = {
  "admin@itaas.demo": "TENANT_ADMIN",
  "hr@itaas.demo": "HR_MANAGER",
  "super@itaas.local": "PLATFORM_OWNER",
  "platform@itaas.local": "PLATFORM_OWNER",
  "hr@itaas.local": "HR_MANAGER",
  "payroll@itaas.local": "PAYROLL_MANAGER",
  "admin@itaas.local": "TENANT_ADMIN",
  "employee@itaas.local": "EMPLOYEE",
  "manager@itaas.local": "MANAGER",
  "customer@itaas.local": "CUSTOMER_USER",
  "deals@itaas.local": "DEALS_MANAGER",
  "leads@itaas.local": "LEADS_MANAGER",
  "recruitment@itaas.local": "RECRUITER",
  "attendance@itaas.local": "ATTENDANCE_MANAGER",
  "finance@itaas.local": "FINANCE_MANAGER",
  "itadmin@itaas.local": "IT_ADMIN",
  "asset@itaas.local": "ASSET_MANAGER",
  "helpdesk@itaas.local": "HELP_DESK_MANAGER",
};

export { PERMISSIONS };
